import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Circle, ChevronDown, ChevronUp,
  Plus, Trash2, Save, Loader2, Send, BookOpen,
  Target, Lightbulb, BarChart3, X, Check, AlertCircle,
  PlayCircle, PenLine,
} from 'lucide-react';
import { plannerAPI } from '../services/api';
import { PageLoader } from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

// ─── Constants ────────────────────────────────────────────────
const STAGE_META = [
  { num: 1, name: 'Tuning In',     color: 'blue',   desc: 'Activate prior knowledge, spark curiosity' },
  { num: 2, name: 'Finding Out',   color: 'purple', desc: 'Research, investigate, gather information' },
  { num: 3, name: 'Sorting Out',   color: 'teal',   desc: 'Process, organise, make sense of findings' },
  { num: 4, name: 'Going Further', color: 'amber',  desc: 'Extend thinking, make connections, apply' },
  { num: 5, name: 'Reflection',    color: 'rose',   desc: 'Assess learning, self-evaluate, look ahead' },
];

const KEY_CONCEPTS = ['Form','Function','Causation','Change','Connection','Perspective','Responsibility','Reflection'];
const ATL_SKILLS   = ['Thinking','Research','Communication','Social','Self-management'];
const LEARNER_PROFILE = ['Inquirers','Knowledgeable','Thinkers','Communicators','Principled','Open-minded','Caring','Risk-takers','Balanced','Reflective'];
const TD_THEMES = [
  'Who We Are',
  'Where We Are in Place and Time',
  'How We Express Ourselves',
  'How the World Works',
  'How We Organize Ourselves',
  'Sharing the Planet',
];

const STAGE_COLORS = {
  blue:   { border: 'border-blue-200',   bg: 'bg-blue-50',   badge: 'bg-blue-100 text-blue-700',   num: 'bg-blue-500'   },
  purple: { border: 'border-purple-200', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700',num: 'bg-purple-500' },
  teal:   { border: 'border-teal-200',   bg: 'bg-teal-50',   badge: 'bg-teal-100 text-teal-700',   num: 'bg-teal-500'   },
  amber:  { border: 'border-amber-200',  bg: 'bg-amber-50',  badge: 'bg-amber-100 text-amber-700', num: 'bg-amber-500'  },
  rose:   { border: 'border-rose-200',   bg: 'bg-rose-50',   badge: 'bg-rose-100 text-rose-700',   num: 'bg-rose-500'   },
};

const STATUS_META = {
  draft:                { label: 'Draft',             color: 'bg-slate-100 text-slate-600' },
  submitted:            { label: 'Submitted',          color: 'bg-blue-100 text-blue-700' },
  under_review:         { label: 'Under Review',       color: 'bg-amber-100 text-amber-700' },
  revision_requested:   { label: 'Revision Requested', color: 'bg-orange-100 text-orange-700' },
  coordinator_approved: { label: 'Coord. Approved',    color: 'bg-teal-100 text-teal-700' },
  vp_approved:          { label: 'VP Approved',        color: 'bg-green-100 text-green-700' },
  rejected:             { label: 'Rejected',           color: 'bg-red-100 text-red-700' },
  completed:            { label: 'Completed',          color: 'bg-purple-100 text-purple-700' },
};

// ─── Helpers ──────────────────────────────────────────────────
function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-500' };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${m.color}`}>{m.label}</span>;
}

function PillToggle({ options, selected = [], onChange, max, colorClass = 'bg-primary-100 text-primary-700 border-primary-300' }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      if (max && selected.length >= max) {
        toast.error(`Maximum ${max} selections allowed`);
        return;
      }
      onChange([...selected, opt]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => toggle(opt)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-all
            ${selected.includes(opt)
              ? colorClass
              : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300'
            }`}>
          {opt}
        </button>
      ))}
    </div>
  );
}

// ─── Stage Panel ──────────────────────────────────────────────
function StagePanel({ stage, planId, editable }) {
  const meta     = STAGE_META[stage.stage_number - 1];
  const colors   = STAGE_COLORS[meta.color];
  const [open,   setOpen]   = useState(false);
  const [form,   setForm]   = useState({
    objective:               stage.objective || '',
    activity_description:    stage.activity_description || '',
    resources:               stage.resources || '',
    expected_outcome:        stage.expected_outcome || '',
    differentiation_strategy: stage.differentiation_strategy || '',
    assessment_opportunity:  stage.assessment_opportunity || '',
    estimated_duration:      stage.estimated_duration || '',
  });
  const [dirty,  setDirty]  = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await plannerAPI.updateStage(planId, stage.stage_number, {
        stage_name: meta.name, ...form,
      });
      setDirty(false);
      toast.success(`Stage ${stage.stage_number} saved`);
    } catch {
      toast.error('Failed to save stage');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border-2 ${stage.is_complete ? 'border-green-300' : colors.border} overflow-hidden`}>
      {/* Accordion header */}
      <button type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${open ? colors.bg : 'bg-white hover:bg-slate-50'}`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${stage.is_complete ? 'bg-green-500' : colors.num}`}>
          {stage.is_complete ? <Check size={13}/> : stage.stage_number}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800">{meta.name}</span>
            {stage.is_complete
              ? <span className="text-xs text-green-600 font-medium">Complete</span>
              : <span className="text-xs text-slate-400">{meta.desc}</span>
            }
            {dirty && <span className="ml-1 w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" title="Unsaved changes"/>}
          </div>
          {stage.estimated_duration && !open && (
            <span className="text-xs text-slate-400">{stage.estimated_duration} min</span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0"/>
               : <ChevronDown size={16} className="text-slate-400 flex-shrink-0"/>}
      </button>

      {/* Accordion body */}
      {open && (
        <div className={`px-5 py-4 space-y-4 border-t ${colors.border} ${colors.bg}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Learning Objective</label>
              <textarea rows={3} value={form.objective}
                onChange={e => set('objective', e.target.value)}
                disabled={!editable}
                placeholder="What will students learn or be able to do?"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Activity Description</label>
              <textarea rows={3} value={form.activity_description}
                onChange={e => set('activity_description', e.target.value)}
                disabled={!editable}
                placeholder="Describe the main student activity…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Resources / Materials</label>
              <textarea rows={2} value={form.resources}
                onChange={e => set('resources', e.target.value)}
                disabled={!editable}
                placeholder="Books, manipulatives, digital tools, worksheets…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Expected Outcome</label>
              <textarea rows={2} value={form.expected_outcome}
                onChange={e => set('expected_outcome', e.target.value)}
                disabled={!editable}
                placeholder="What should students be able to demonstrate?"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Differentiation Strategy</label>
              <textarea rows={2} value={form.differentiation_strategy}
                onChange={e => set('differentiation_strategy', e.target.value)}
                disabled={!editable}
                placeholder="How will you support diverse learners?"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Assessment Opportunity</label>
              <textarea rows={2} value={form.assessment_opportunity}
                onChange={e => set('assessment_opportunity', e.target.value)}
                disabled={!editable}
                placeholder="How will you check for understanding?"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50 disabled:text-slate-400"/>
            </div>
          </div>
          <div className="w-36">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Duration (minutes)</label>
            <input type="number" min="5" max="120" value={form.estimated_duration}
              onChange={e => set('estimated_duration', e.target.value)}
              disabled={!editable}
              placeholder="e.g. 15"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
          </div>
          {editable && (
            <div className="flex justify-end">
              <button onClick={save} disabled={saving || !dirty}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-all">
                {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                {saving ? 'Saving…' : dirty ? 'Save Stage' : 'Saved'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Unit Planner Tab ─────────────────────────────────────────
function UnitPlannerTab({ planId, initial, editable }) {
  const [form, setForm] = useState({
    unit_title:                initial?.unit_title || '',
    transdisciplinary_theme:   initial?.transdisciplinary_theme || '',
    central_idea:              initial?.central_idea || '',
    learning_objectives:       initial?.learning_objectives || '',
    success_criteria:          initial?.success_criteria || '',
    prior_knowledge:           initial?.prior_knowledge || '',
    student_questions:         initial?.student_questions || '',
    unit_duration_weeks:       initial?.unit_duration_weeks || '',
    lines_of_inquiry:          tryParse(initial?.lines_of_inquiry, []),
    key_concepts:              tryParse(initial?.key_concepts, []),
    atl_skills:                tryParse(initial?.atl_skills, []),
    learner_profile_attributes:tryParse(initial?.learner_profile_attributes, []),
  });
  const [newLOI,  setNewLOI]  = useState('');
  const [dirty,   setDirty]   = useState(false);
  const [saving,  setSaving]  = useState(false);

  function tryParse(val, fallback) {
    if (!val) return fallback;
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return fallback; }
  }

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const addLOI = () => {
    if (!newLOI.trim()) return;
    set('lines_of_inquiry', [...form.lines_of_inquiry, newLOI.trim()]);
    setNewLOI('');
  };
  const removeLOI = (i) => set('lines_of_inquiry', form.lines_of_inquiry.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.unit_title.trim()) return toast.error('Unit title is required');
    setSaving(true);
    try {
      await plannerAPI.updateUnit(planId, form);
      setDirty(false);
      toast.success('Unit planner saved');
    } catch {
      toast.error('Failed to save unit planner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-1">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Unit Title <span className="text-red-500">*</span></label>
          <input type="text" value={form.unit_title}
            onChange={e => set('unit_title', e.target.value)}
            disabled={!editable}
            placeholder="e.g. Forces and Motion"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Transdisciplinary Theme</label>
          <select value={form.transdisciplinary_theme}
            onChange={e => set('transdisciplinary_theme', e.target.value)}
            disabled={!editable}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50">
            <option value="">Select theme</option>
            {TD_THEMES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Central Idea</label>
        <textarea rows={2} value={form.central_idea}
          onChange={e => set('central_idea', e.target.value)}
          disabled={!editable}
          placeholder="A single, transferable statement that encapsulates the unit of inquiry…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
      </div>

      {/* Lines of Inquiry */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">Lines of Inquiry</label>
        <div className="space-y-2 mb-2">
          {form.lines_of_inquiry.map((loi, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <span className="w-5 h-5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
              <span className="flex-1 text-sm text-slate-700">{loi}</span>
              {editable && (
                <button onClick={() => removeLOI(i)} className="text-slate-300 hover:text-red-400 transition-colors">
                  <X size={14}/>
                </button>
              )}
            </div>
          ))}
        </div>
        {editable && (
          <div className="flex gap-2">
            <input value={newLOI} onChange={e => setNewLOI(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLOI(); }}}
              placeholder="Add a line of inquiry and press Enter…"
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            <button onClick={addLOI}
              className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 text-sm font-medium">
              <Plus size={14}/>
            </button>
          </div>
        )}
      </div>

      {/* Key Concepts */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">Key Concepts <span className="text-slate-400 font-normal">(select up to 3)</span></label>
        <PillToggle
          options={KEY_CONCEPTS}
          selected={form.key_concepts}
          onChange={v => set('key_concepts', v)}
          max={3}
          colorClass="bg-blue-100 text-blue-700 border-blue-300"/>
      </div>

      {/* ATL Skills */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">ATL Skills</label>
        <PillToggle
          options={ATL_SKILLS}
          selected={form.atl_skills}
          onChange={v => set('atl_skills', v)}
          colorClass="bg-purple-100 text-purple-700 border-purple-300"/>
      </div>

      {/* Learner Profile */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">Learner Profile Attributes</label>
        <PillToggle
          options={LEARNER_PROFILE}
          selected={form.learner_profile_attributes}
          onChange={v => set('learner_profile_attributes', v)}
          colorClass="bg-teal-100 text-teal-700 border-teal-300"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Learning Objectives</label>
          <textarea rows={3} value={form.learning_objectives}
            onChange={e => set('learning_objectives', e.target.value)}
            disabled={!editable}
            placeholder="By the end of this unit, students will…"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Success Criteria</label>
          <textarea rows={3} value={form.success_criteria}
            onChange={e => set('success_criteria', e.target.value)}
            disabled={!editable}
            placeholder="Students will be able to demonstrate / explain / create…"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Prior Knowledge Required</label>
          <textarea rows={2} value={form.prior_knowledge}
            onChange={e => set('prior_knowledge', e.target.value)}
            disabled={!editable}
            placeholder="What should students already know before this unit?"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Student Questions / Wonderings</label>
          <textarea rows={2} value={form.student_questions}
            onChange={e => set('student_questions', e.target.value)}
            disabled={!editable}
            placeholder="Questions students have generated about this topic…"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
        </div>
      </div>

      <div className="w-48">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Unit Duration (weeks)</label>
        <input type="number" min="1" max="20" value={form.unit_duration_weeks}
          onChange={e => set('unit_duration_weeks', e.target.value)}
          disabled={!editable}
          placeholder="e.g. 6"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-slate-50"/>
      </div>

      {editable && (
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {saving ? 'Saving…' : 'Save Unit Planner'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Assessments Tab ──────────────────────────────────────────
function AssessmentsTab({ planId, initial, editable }) {
  const [list,    setList]    = useState(initial || []);
  const [showAdd, setShowAdd] = useState(false);
  const [form,    setForm]    = useState({ assessment_type: 'formative', assessment_title: '', rubric: '', success_criteria: '', evidence_required: '', strategy: '', assessment_date: '' });
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.assessment_title.trim()) return toast.error('Assessment title is required');
    setSaving(true);
    try {
      const r = await plannerAPI.addAssessment(planId, form);
      setList(l => [...l, r.data]);
      setShowAdd(false);
      setForm({ assessment_type: 'formative', assessment_title: '', rubric: '', success_criteria: '', evidence_required: '', strategy: '', assessment_date: '' });
      toast.success('Assessment added');
    } catch {
      toast.error('Failed to add assessment');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this assessment?')) return;
    setDeleting(id);
    try {
      await plannerAPI.deleteAssessment(planId, id);
      setList(l => l.filter(a => a.id !== id));
      toast.success('Assessment deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const TYPE_META = {
    formative:  { label: 'Formative',   color: 'bg-blue-100 text-blue-700' },
    summative:  { label: 'Summative',   color: 'bg-purple-100 text-purple-700' },
    diagnostic: { label: 'Diagnostic',  color: 'bg-amber-100 text-amber-700' },
  };

  return (
    <div className="space-y-4">
      {list.length === 0 && !showAdd && (
        <div className="py-10 text-center text-slate-400">
          <BarChart3 size={32} className="mx-auto mb-2 opacity-40"/>
          <p className="text-sm">No assessments added yet</p>
        </div>
      )}

      {list.map(a => (
        <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_META[a.assessment_type]?.color || 'bg-slate-100 text-slate-600'}`}>
                  {TYPE_META[a.assessment_type]?.label || a.assessment_type}
                </span>
                {a.assessment_date && (
                  <span className="text-xs text-slate-400">
                    {new Date(a.assessment_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}
                  </span>
                )}
              </div>
              <p className="font-semibold text-slate-800 text-sm">{a.assessment_title}</p>
              {a.success_criteria && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Criteria:</span> {a.success_criteria}</p>}
              {a.strategy && <p className="text-xs text-slate-500"><span className="font-medium">Strategy:</span> {a.strategy}</p>}
            </div>
            {editable && (
              <button onClick={() => remove(a.id)} disabled={deleting === a.id}
                className="p-1.5 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors flex-shrink-0">
                {deleting === a.id ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
              </button>
            )}
          </div>
        </div>
      ))}

      {showAdd && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-slate-700">New Assessment</h4>
            <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Type</label>
              <select value={form.assessment_type} onChange={e => setF('assessment_type', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white">
                <option value="formative">Formative</option>
                <option value="summative">Summative</option>
                <option value="diagnostic">Diagnostic</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Assessment Date</label>
              <input type="date" value={form.assessment_date} onChange={e => setF('assessment_date', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.assessment_title} onChange={e => setF('assessment_title', e.target.value)}
              placeholder="e.g. Unit test — Forces and Motion"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Success Criteria</label>
              <textarea rows={2} value={form.success_criteria} onChange={e => setF('success_criteria', e.target.value)}
                placeholder="Students can demonstrate…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Assessment Strategy</label>
              <textarea rows={2} value={form.strategy} onChange={e => setF('strategy', e.target.value)}
                placeholder="Exit tickets, rubric, observation, quiz…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Rubric / Criteria</label>
              <textarea rows={2} value={form.rubric} onChange={e => setF('rubric', e.target.value)}
                placeholder="Describe the rubric or grading criteria…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Evidence Required</label>
              <textarea rows={2} value={form.evidence_required} onChange={e => setF('evidence_required', e.target.value)}
                placeholder="Portfolio entry, written reflection, photo…"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={add} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
              Add Assessment
            </button>
          </div>
        </div>
      )}

      {editable && !showAdd && (
        <button onClick={() => setShowAdd(true)}
          className="w-full py-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-primary-300 hover:text-primary-600 text-sm font-medium flex items-center justify-center gap-2 transition-all">
          <Plus size={15}/> Add Assessment
        </button>
      )}
    </div>
  );
}

// ─── Execution Tab ────────────────────────────────────────────
function ExecutionTab({ planId, initial }) {
  const [form, setForm] = useState({
    execution_status:     initial?.execution_status     || 'completed',
    execution_date:       initial?.execution_date?.slice(0, 10) || '',
    execution_start_time: initial?.execution_start_time || '',
    execution_end_time:   initial?.execution_end_time   || '',
    completion_pct:       initial?.completion_pct       ?? 100,
    teacher_notes:        initial?.teacher_notes        || '',
    reschedule_date:      initial?.reschedule_date?.slice(0, 10) || '',
    reschedule_reason:    initial?.reschedule_reason    || '',
  });
  const [dirty,  setDirty]  = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(!!initial?.id);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!form.execution_date) return toast.error('Execution date is required');
    setSaving(true);
    try {
      await plannerAPI.saveExecution(planId, form);
      setDirty(false);
      setSaved(true);
      toast.success('Execution recorded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save execution');
    } finally {
      setSaving(false);
    }
  };

  const STATUS_OPTS = [
    { value: 'completed',          label: 'Completed',          color: 'text-green-700 bg-green-50 border-green-300' },
    { value: 'partially_completed',label: 'Partially Completed', color: 'text-amber-700 bg-amber-50 border-amber-300' },
    { value: 'not_conducted',      label: 'Not Conducted',      color: 'text-red-700 bg-red-50 border-red-300' },
    { value: 'rescheduled',        label: 'Rescheduled',        color: 'text-blue-700 bg-blue-50 border-blue-300' },
  ];

  return (
    <div className="space-y-5">
      {saved && !dirty && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle size={15}/> Execution recorded
        </div>
      )}

      {/* Status selector */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">Execution Status <span className="text-red-500">*</span></label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {STATUS_OPTS.map(opt => (
            <button key={opt.value} type="button" onClick={() => set('execution_status', opt.value)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all
                ${form.execution_status === opt.value ? opt.color : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Execution Date <span className="text-red-500">*</span></label>
          <input type="date" value={form.execution_date} onChange={e => set('execution_date', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Start Time</label>
          <input type="time" value={form.execution_start_time} onChange={e => set('execution_start_time', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">End Time</label>
          <input type="time" value={form.execution_end_time} onChange={e => set('execution_end_time', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">
          Completion % <span className="text-slate-400 font-normal">({form.completion_pct}%)</span>
        </label>
        <input type="range" min="0" max="100" step="5" value={form.completion_pct}
          onChange={e => set('completion_pct', parseInt(e.target.value))}
          className="w-full accent-primary-600"/>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>0%</span><span>50%</span><span>100%</span>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Teacher Notes</label>
        <textarea rows={4} value={form.teacher_notes} onChange={e => set('teacher_notes', e.target.value)}
          placeholder="How did the lesson go? Any highlights or issues…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      {form.execution_status === 'rescheduled' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rescheduled Date</label>
            <input type="date" value={form.reschedule_date} onChange={e => set('reschedule_date', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Reason for Rescheduling</label>
            <input type="text" value={form.reschedule_reason} onChange={e => set('reschedule_reason', e.target.value)}
              placeholder="e.g. School event, power cut…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white"/>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <button onClick={save} disabled={saving || !dirty}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save Execution'}
        </button>
      </div>
    </div>
  );
}

// ─── Reflection Tab ───────────────────────────────────────────
function ReflectionTab({ planId, initial }) {
  const [form, setForm] = useState({
    what_worked_well:          initial?.what_worked_well          || '',
    challenges_faced:          initial?.challenges_faced          || '',
    student_understanding:     initial?.student_understanding     || 'medium',
    students_needing_support:  initial?.students_needing_support  || '',
    changes_for_next_year:     initial?.changes_for_next_year     || '',
    resources_needed:          initial?.resources_needed          || '',
  });
  const [dirty,  setDirty]  = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(!!initial?.id);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!form.what_worked_well.trim() || !form.challenges_faced.trim()) {
      return toast.error('Please fill what worked well and challenges faced');
    }
    setSaving(true);
    try {
      await plannerAPI.saveReflection(planId, form);
      setDirty(false);
      setSaved(true);
      toast.success('Reflection saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const UNDERSTANDING_OPTS = [
    { value: 'high',   label: 'High',   color: 'border-green-400 bg-green-50 text-green-700' },
    { value: 'mixed',  label: 'Mixed',  color: 'border-blue-400 bg-blue-50 text-blue-700' },
    { value: 'medium', label: 'Medium', color: 'border-amber-400 bg-amber-50 text-amber-700' },
    { value: 'low',    label: 'Low',    color: 'border-red-400 bg-red-50 text-red-700' },
  ];

  return (
    <div className="space-y-5">
      {saved && !dirty && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle size={15}/> Reflection submitted
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">What Worked Well? <span className="text-red-500">*</span></label>
        <textarea rows={4} value={form.what_worked_well} onChange={e => set('what_worked_well', e.target.value)}
          placeholder="Describe the activities, strategies, or moments that were effective…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Challenges Faced <span className="text-red-500">*</span></label>
        <textarea rows={4} value={form.challenges_faced} onChange={e => set('challenges_faced', e.target.value)}
          placeholder="What didn't go as planned? What obstacles did you encounter?…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">Overall Student Understanding</label>
        <div className="flex gap-2 flex-wrap">
          {UNDERSTANDING_OPTS.map(opt => (
            <button key={opt.value} type="button" onClick={() => set('student_understanding', opt.value)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all
                ${form.student_understanding === opt.value ? opt.color : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Students Needing Additional Support</label>
        <textarea rows={2} value={form.students_needing_support} onChange={e => set('students_needing_support', e.target.value)}
          placeholder="Names or groups of students who need follow-up…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Changes for Next Year / Next Lesson</label>
        <textarea rows={3} value={form.changes_for_next_year} onChange={e => set('changes_for_next_year', e.target.value)}
          placeholder="What would you do differently next time?…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Resources / Support Needed</label>
        <textarea rows={2} value={form.resources_needed} onChange={e => set('resources_needed', e.target.value)}
          placeholder="Any materials, CPD, or tools you need…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"/>
      </div>

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <button onClick={save} disabled={saving || !dirty}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save Reflection'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Editor Page ─────────────────────────────────────────
export default function LessonPlannerEditor() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [plan,     setPlan]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('stages');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    plannerAPI.getPlan(id)
      .then(r => setPlan(r.data))
      .catch(() => { toast.error('Failed to load plan'); navigate('/lesson-planner'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const editable = plan && ['draft', 'revision_requested'].includes(plan.status);

  const handleSubmit = async () => {
    if (!window.confirm('Submit this plan for coordinator review?')) return;
    setSubmitting(true);
    try {
      await plannerAPI.submitPlan(id);
      toast.success('Plan submitted for review');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PageLoader/>;
  if (!plan) return null;

  const stages = plan.stages || [];
  const unit   = plan.unit_planner;
  const assessments  = plan.assessments  || [];
  const execution    = plan.execution    || null;
  const reflection   = plan.reflection   || null;

  const completedStages = stages.filter(s => s.is_complete).length;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* ── Header bar ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/lesson-planner')}
            className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
            <ArrowLeft size={18}/>
          </button>
          <div>
            <h2 className="text-xl font-display font-bold text-slate-800 leading-tight">{plan.lesson_title}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <StatusBadge status={plan.status}/>
              <span className="text-xs text-slate-400">{plan.grade} · {plan.subject_name} · Week {plan.week_number}</span>
              {plan.is_late_submission ? <span className="text-xs text-red-500 font-semibold">⚠ Late submission</span> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Completion ring */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-200 text-sm">
            <div className="w-4 h-4 relative">
              <svg viewBox="0 0 16 16" className="w-4 h-4 -rotate-90">
                <circle cx="8" cy="8" r="6" fill="none" stroke="#e2e8f0" strokeWidth="2.5"/>
                <circle cx="8" cy="8" r="6" fill="none" stroke="#22c55e" strokeWidth="2.5"
                  strokeDasharray={`${2 * Math.PI * 6}`}
                  strokeDashoffset={`${2 * Math.PI * 6 * (1 - (plan.completion_pct || 0) / 100)}`}
                  strokeLinecap="round"/>
              </svg>
            </div>
            <span className="text-slate-600 font-medium">{plan.completion_pct || 0}% complete</span>
          </div>
          {editable && (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60">
              {submitting ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
              {submitting ? 'Submitting…' : 'Submit for Review'}
            </button>
          )}
        </div>
      </div>

      {/* ── Plan info summary ── */}
      {plan.coordinator_review && (
        <div className={`rounded-xl p-4 border flex items-start gap-3
          ${plan.status === 'revision_requested' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
          <AlertCircle size={16} className={`flex-shrink-0 mt-0.5 ${plan.status === 'revision_requested' ? 'text-orange-500' : 'text-green-500'}`}/>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {plan.status === 'revision_requested' ? 'Revision Requested' : 'Coordinator Review'}
              <span className="text-xs font-normal text-slate-500 ml-2">by {plan.coordinator_name}</span>
            </p>
            <p className="text-sm text-slate-600 mt-0.5">{plan.coordinator_review.review_comments}</p>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex gap-1 p-3 border-b border-slate-100">
          {[
            { key: 'stages',     label: 'Lesson Stages', icon: BookOpen,     badge: `${completedStages}/5` },
            { key: 'unit',       label: 'Unit Planner',  icon: Target,       badge: unit?.unit_title ? '✓' : null },
            { key: 'assessments',label: 'Assessments',   icon: BarChart3,    badge: assessments.length || null },
            { key: 'execution',  label: 'Execution',     icon: PlayCircle,   badge: execution?.id ? '✓' : null },
            { key: 'reflection', label: 'Reflection',    icon: PenLine,      badge: reflection?.id ? '✓' : null },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all
                ${tab === t.key ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <t.icon size={14}/>
              {t.label}
              {t.badge !== null && t.badge !== undefined && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold
                  ${tab === t.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}

          {!editable && (
            <span className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 bg-slate-50 rounded-xl border border-slate-200">
              <AlertCircle size={12}/> Read-only — plan is {STATUS_META[plan.status]?.label}
            </span>
          )}
        </div>

        <div className="p-5">
          {tab === 'stages' && (
            <div className="space-y-3">
              {stages.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">No stages found. Try refreshing.</p>
              ) : (
                stages.map(s => (
                  <StagePanel key={s.id || s.stage_number} stage={s} planId={id} editable={editable}/>
                ))
              )}
            </div>
          )}
          {tab === 'unit' && (
            <UnitPlannerTab planId={id} initial={unit} editable={editable}/>
          )}
          {tab === 'assessments' && (
            <AssessmentsTab planId={id} initial={assessments} editable={editable}/>
          )}
          {tab === 'execution' && (
            <ExecutionTab planId={id} initial={execution}/>
          )}
          {tab === 'reflection' && (
            <ReflectionTab planId={id} initial={reflection}/>
          )}
        </div>
      </div>

      {/* ── Status History ── */}
      {plan.status_history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h4 className="text-sm font-bold text-slate-700 mb-3">Activity Log</h4>
          <div className="space-y-3">
            {plan.status_history.map((h, i) => (
              <div key={h.id || i} className="flex items-start gap-3 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0"/>
                <div>
                  <span className="font-semibold text-slate-700">{h.changed_by_name || 'System'}</span>
                  <span className="text-slate-400"> → </span>
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${STATUS_META[h.to_status]?.color || 'bg-slate-100'}`}>{h.to_status}</span>
                  <span className="text-slate-400 ml-2">
                    {new Date(h.changed_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit', hour:'2-digit', minute:'2-digit' })}
                  </span>
                  {h.reason && <p className="text-slate-500 mt-0.5 italic">"{h.reason}"</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
